import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Anchor, Check, LayoutList, Map as MapIcon, MoveHorizontal, Play, RotateCcw, X } from "lucide-react";
import { MAP_IMG, PLACES } from "@/lib/bibleMap";
import { Birds, Clouds, Glints, MapSound, Scenes } from "@/components/cast/MapLife";

export type Stop = { key: string; place: string; label: string; sub?: string; href: string; done: boolean; image?: string };
const AR = 848 / 1264;
const clampPct = (v: number) => Math.min(94, Math.max(6, v));

// Spreads stops that share a spot into rings around the real location
function layout(stops: Stop[], minGap: number) {
  const out: { x: number; y: number }[] = [];
  stops.forEach((s) => {
    const [px, py] = PLACES[s.place] || [50, 50];
    let x = px, y = py, k = 0;
    while (out.some((o) => Math.hypot(o.x - x, (o.y - y) * AR) < minGap) && k < 80) {
      k += 1;
      const ring = 1 + Math.floor(k / 8), r = minGap * ring, a = k * (Math.PI / (3 * ring)) + ring;
      x = px + Math.cos(a) * r; y = py + (Math.sin(a) * r) / AR;
    }
    out.push({ x, y });
  });
  return out;
}

function Harbour({ x, y }: { x: number; y: number }) {
  return (
    <div className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{ left: `${x}%`, top: `${y}%` }} data-testid="map-harbour">
      <svg viewBox="0 0 80 60" className="h-12 w-16 drop-shadow-md">
        <ellipse cx="40" cy="48" rx="36" ry="9" fill="#7FB3C8" opacity=".7" />
        <rect x="8" y="36" width="52" height="7" rx="2" fill="#8C5E58" /><rect x="14" y="43" width="4" height="10" fill="#5B4636" /><rect x="48" y="43" width="4" height="10" fill="#5B4636" />
        <rect x="60" y="14" width="10" height="30" rx="2" fill="#FFF8EA" stroke="#8C5E58" strokeWidth="2" /><rect x="60" y="22" width="10" height="5" fill="#C86D51" /><path d="M58 14 L65 4 L72 14 Z" fill="#C86D51" />
      </svg>
    </div>
  );
}

function PreviewCard({ stop, onClose }: { stop: Stop; onClose: () => void }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }} data-testid="map-preview-card"
      className="absolute bottom-3 left-3 right-3 z-30 flex gap-3 rounded-2xl bg-white/95 p-3 shadow-xl ring-1 ring-[#E2DBCE] backdrop-blur sm:right-auto sm:w-96">
      {stop.image && <img src={stop.image} alt="" className="h-20 w-20 shrink-0 rounded-xl object-cover" data-testid="map-preview-image" />}
      <div className="min-w-0 flex-1">
        <p className="font-display text-lg font-semibold leading-tight text-slate-900" data-testid="map-preview-title">{stop.label}</p>
        <p className="mt-0.5 truncate text-xs text-slate-500">{PLACES[stop.place]?.[2]}{stop.sub ? ` · ${stop.sub}` : ""}</p>
        <Link to={stop.href} data-testid="map-preview-start" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#C86D51] px-4 py-1.5 text-sm font-semibold text-white transition-transform hover:scale-105">
          <Play className="h-3.5 w-3.5" />{stop.done ? "Visit again" : "Start"}
        </Link>
      </div>
      <button onClick={onClose} data-testid="map-preview-close" aria-label="Close" className="self-start rounded-full p-1 text-slate-400 hover:bg-[#F3ECE0]"><X className="h-4 w-4" /></button>
    </motion.div>
  );
}

export type MapBoat = { key: string; idx: number; color: string; name: string };

function Boat({ sail = "#F5C98B", small }: { sail?: string; small?: boolean }) {
  return (
    <svg viewBox="0 0 120 100" className={`bd-bob drop-shadow-lg ${small ? "h-10 w-12" : "h-12 w-14"}`}>
      <path d="M58 8 L58 70 L18 70 Z" fill="#FFF8EC" stroke="#8C5E58" strokeWidth="2" /><path d="M62 18 L62 70 L94 70 Z" fill={sail} stroke="#8C5E58" strokeWidth="2" />
      <path d="M10 74 L110 74 L96 92 L24 92 Z" fill="#8C5E58" /><line x1="60" y1="4" x2="60" y2="74" stroke="#5B4636" strokeWidth="3" />
    </svg>
  );
}

export function BibleMap({ stops, zoom, testId = "bible-map", celebrateKey, boats }: { stops: Stop[]; zoom?: boolean; testId?: string; celebrateKey?: string; boats?: MapBoat[] }) {
  const { pts, scale, ox, oy } = useMemo(() => {
    const raw = stops.map((s) => PLACES[s.place] || [50, 50]);
    const xs = raw.map((p) => p[0]), ys = raw.map((p) => p[1]);
    const w = Math.max(...xs) - Math.min(...xs) + 16, h = Math.max(...ys) - Math.min(...ys) + 16;
    const sc = zoom ? Math.max(1, Math.min(2.6, 100 / w, 100 / h)) : 1;
    const pts = layout(stops, 4.6 / sc);
    const cx = (Math.max(...xs) + Math.min(...xs)) / 2, cy = (Math.max(...ys) + Math.min(...ys)) / 2;
    const clamp = (v: number) => Math.min(0, Math.max(100 - 100 * sc, v));
    return { pts, scale: sc, ox: clamp(50 - cx * sc), oy: clamp(50 - cy * sc) };
  }, [stops, zoom]);
  const allDone = stops.length > 0 && stops.every((s) => s.done);
  const curIdx = allDone ? stops.length - 1 : Math.max(0, stops.findIndex((s) => !s.done));
  const tx = (x: number) => x * scale + ox, ty = (y: number) => y * scale + oy;
  const curve = (ps: { x: number; y: number }[]) => ps.map((p, i) => {
    if (!i) return `M ${tx(p.x)} ${ty(p.y) * AR}`;
    const q = ps[i - 1], mx = (tx(q.x) + tx(p.x)) / 2 + (i % 2 ? 3 : -3), my = ((ty(q.y) + ty(p.y)) / 2) * AR + (i % 2 ? -2 : 2);
    return `Q ${mx} ${my} ${tx(p.x)} ${ty(p.y) * AR}`;
  }).join(" ");
  const path = curve(pts);
  const lastDone = stops.reduce((m, s, i) => (s.done ? i : m), -1);
  const sailEnd = Math.max(curIdx, lastDone);
  const sailed = curve(pts.slice(0, sailEnd + 1));
  const sailTime = Math.min(6, 0.6 + (curIdx + 1) * 0.35);
  const pos = (x: number, y: number, dx = 0, dy = 0) => ({ left: `${tx(x) + dx}%`, top: `${ty(y) + dy}%` });
  const last = pts[pts.length - 1];
  const harbour = allDone && celebrateKey && last ? { x: clampPct(tx(last.x) + 7), y: clampPct(ty(last.y) + 9) } : null;
  const trail = pts.slice(0, curIdx + 1).map((p) => ({ x: tx(p.x), y: ty(p.y) }));
  if (harbour) trail.push(harbour);

  const [selected, setSelected] = useState<number | null>(null);
  const [run, setRun] = useState(0);
  const boatRef = useRef<HTMLDivElement>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!harbour || !celebrateKey || localStorage.getItem(celebrateKey) || !box.current) return;
    const io = new IntersectionObserver(([en]) => {
      if (!en.isIntersecting || localStorage.getItem(celebrateKey)) return;
      localStorage.setItem(celebrateKey, "1");
      setRun(1);
      io.disconnect();
    }, { threshold: 0.6 });
    io.observe(box.current);
    return () => io.disconnect();
  }, [allDone, celebrateKey]); // eslint-disable-line
  const cheer = () => {
    if (!run || !boatRef.current) return;
    const r = boatRef.current.getBoundingClientRect();
    const origin = { x: (r.left + r.width / 2) / innerWidth, y: (r.top + r.height / 2) / innerHeight };
    confetti({ particleCount: 120, spread: 90, origin, colors: ["#C86D51", "#F5C98B", "#5B7065", "#7FB3C8", "#FFF8EA"], disableForReducedMotion: true });
  };

  const wrap = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  useEffect(() => {
    const w = wrap.current;
    if (!w || !trail.length) return;
    const inner = w.firstElementChild as HTMLElement;
    setScrollable(w.scrollWidth > w.clientWidth + 4);
    const focus = boats?.length ? tx(pts[Math.min(Math.max(...boats.map((b) => b.idx)), pts.length - 1)].x) : trail[trail.length - 1].x;
    w.scrollLeft = (focus / 100) * inner.offsetWidth - w.clientWidth / 2;
  }, [pts, curIdx]); // eslint-disable-line

  return (
    <div className="relative" ref={box}>
      <div ref={wrap} className="-mx-5 overflow-x-auto px-5 sm:mx-0 sm:px-0" data-testid={testId}>
        <div className="relative min-w-[860px] overflow-hidden rounded-3xl border border-[#E2DBCE] shadow-xl" style={{ aspectRatio: "1264 / 848" }} onClick={() => setSelected(null)}>
          <img src={MAP_IMG} alt="Painted map of the Bible lands" className="absolute max-w-none origin-top-left" style={{ width: `${scale * 100}%`, left: `${ox}%`, top: `${oy}%` }} />
          <svg viewBox={`0 0 100 ${100 * AR}`} className="absolute inset-0 h-full w-full" preserveAspectRatio="none">
            <path d={path} fill="none" stroke="#FFF8EA" strokeWidth="0.9" strokeLinecap="round" opacity=".9" />
            <path d={path} fill="none" stroke="#C86D51" strokeWidth="0.35" strokeDasharray="1 0.8" strokeLinecap="round" />
            {sailEnd > 0 && <>
              <motion.path key={`g${run}`} d={sailed} fill="none" stroke="#FFD98A" strokeWidth="1.6" strokeLinecap="round" opacity=".55" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: sailTime, ease: "easeInOut" }} data-testid="map-sailed-path" />
              <motion.path key={`s${run}`} d={sailed} fill="none" stroke="#E0A93B" strokeWidth="0.45" strokeLinecap="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: sailTime, ease: "easeInOut" }} />
            </>}
            {harbour && <path d={`M ${tx(last.x)} ${ty(last.y) * AR} L ${harbour.x} ${harbour.y * AR}`} fill="none" stroke="#5B7065" strokeWidth="0.35" strokeDasharray="1 0.8" />}
          </svg>
          <Glints pos={pos} />
          <Scenes pos={pos} avoid={pts.map((p) => ({ x: tx(p.x), y: ty(p.y) }))} />
          <Clouds />
          <Birds />
          {harbour && <Harbour {...harbour} />}
          {stops.map((s, i) => (
            <button key={s.key} data-testid={`map-stop-${s.key}`} title={`${s.label} · ${PLACES[s.place]?.[2] || ""}`} onClick={(e) => { e.stopPropagation(); setSelected(i); }}
              className="group absolute z-10 -translate-x-1/2 -translate-y-1/2" style={{ left: `${tx(pts[i].x)}%`, top: `${ty(pts[i].y)}%` }}>
              {i === curIdx && !allDone && !boats && <span className="ml-pulse pointer-events-none absolute inset-0 rounded-full" data-testid="map-next-pulse" />}
              <span className={`relative flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold shadow-md ring-2 transition-transform group-hover:scale-125 ${selected === i ? "scale-125 ring-[#1E293B]" : "ring-white"} ${s.done ? "bg-[#5B7065] text-white" : i === curIdx ? "bg-[#C86D51] text-white" : "bg-[#FFF8EA] text-slate-700"}`}>
                {s.done ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span data-testid={i === curIdx && !allDone && !boats ? "map-current-label" : undefined} className={`pointer-events-none absolute left-1/2 top-9 w-max max-w-[180px] -translate-x-1/2 rounded-lg bg-white/95 px-2 py-1 text-center text-[11px] font-semibold leading-tight text-slate-800 shadow transition-opacity ${selected === i ? "" : "[@media(hover:hover)]:group-hover:opacity-100"} ${i === curIdx && !allDone && !boats && selected === null ? "opacity-100" : "opacity-0"}`}>
                {s.label}<span className="block font-normal text-slate-500">{PLACES[s.place]?.[2]}{s.sub ? ` · ${s.sub}` : ""}</span>
              </span>
            </button>
          ))}
          {boats ? boats.map((b, k) => {
            const end = Math.min(b.idx, pts.length - 1), same = boats.filter((o, j) => j < k && o.idx === b.idx).length;
            const path = pts.slice(0, end + 1);
            return (
              <motion.div key={b.key} className="pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-[110%]" data-testid={`family-boat-${b.key}`}
                initial={{ left: `${tx(path[0].x)}%`, top: `${ty(path[0].y)}%` }}
                animate={{ left: path.map((q) => `${tx(q.x) + same * 3.2}%`), top: path.map((q) => `${ty(q.y) - same * 1.5}%`) }}
                transition={{ duration: Math.min(6, 0.8 + path.length * 0.3), ease: "easeInOut", delay: k * 0.4 }}>
                <div className="flex flex-col items-center">
                  <span className="mb-[-4px] whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold text-white shadow" style={{ background: b.color }}>{b.name}</span>
                  <Boat sail={b.color} small />
                </div>
              </motion.div>
            );
          }) : <motion.div key={run} ref={boatRef} className="pointer-events-none absolute z-20 -translate-x-[15%] -translate-y-[115%]" data-testid="map-boat"
            initial={{ left: `${trail[0].x}%`, top: `${trail[0].y}%` }}
            animate={{ left: trail.map((p) => `${p.x}%`), top: trail.map((p) => `${p.y}%`) }}
            transition={{ duration: Math.min(6, 0.6 + trail.length * 0.35), ease: "easeInOut" }}
            onAnimationComplete={cheer}>
            <Boat />
          </motion.div>}
        </div>
      </div>
      {harbour && (
        <div className="absolute left-3 top-3 z-30 flex items-center gap-2 rounded-full bg-white/95 py-1 pl-3 pr-1 shadow-lg ring-1 ring-[#E2DBCE]" data-testid="map-journey-complete">
          <Anchor className="h-4 w-4 text-[#5B7065]" /><span className="whitespace-nowrap text-xs font-semibold text-slate-800 sm:text-sm">Journey complete<span className="hidden sm:inline"> · safe in the harbour</span></span>
          <button onClick={() => setRun((r) => r + 1)} data-testid="map-replay-celebration" className="flex items-center gap-1 rounded-full bg-[#1E293B] px-3 py-1 text-xs font-semibold text-white hover:bg-slate-700">
            <RotateCcw className="h-3.5 w-3.5" />Replay
          </button>
        </div>
      )}
      <MapSound />
      <AnimatePresence>{selected !== null && stops[selected] && <PreviewCard key={stops[selected].key} stop={stops[selected]} onClose={() => setSelected(null)} />}</AnimatePresence>
      {scrollable && selected === null && (
        <span data-testid="map-swipe-hint" className="pointer-events-none absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-700 shadow">
          <MoveHorizontal className="h-3.5 w-3.5" />Swipe to explore
        </span>
      )}
    </div>
  );
}

export function useViewMode(): [string, (v: string) => void] {
  const [view, setView] = useState(() => localStorage.getItem("cast_view") || "map");
  return [view, (v: string) => { localStorage.setItem("cast_view", v); setView(v); }];
}

export function ViewToggle({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="inline-flex rounded-full border border-[#E2DBCE] bg-white p-1" data-testid="view-toggle">
      {[["map", "Map", MapIcon], ["cards", "Standard", LayoutList]].map(([k, l, Icon]: any) => (
        <button key={k} data-testid={`view-toggle-${k}`} onClick={() => onChange(k)}
          className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${value === k ? "bg-[#1E293B] text-white" : "text-slate-600 hover:bg-[#F3ECE0]"}`}><Icon className="h-4 w-4" />{l}</button>
      ))}
    </div>
  );
}
